import { useMemo, useState, type ReactNode } from 'react';
import {
  RentalSearchBar,
  useRentalMyLocation,
  useTravelerFieldSearch,
  useRemoteSearch,
  type DateRange,
  type PersonItem,
  type PlaceFieldControlledSearch,
  type PlaceSuggestion,
  type RentalPlaceAt,
  type RentalSearchCopy,
  type RentalSearchValue,
} from '@voll-intelligence/intelligence-ui-kit';
import {
  formatClockTime,
  formatDateRange,
  formatDateTimeWindow,
  formatDayMonth,
  formatWeekdayDayMonth,
  parseDateOnly,
  parseLocalDateTime,
} from '@voll-intelligence/dates';
import { useGenUiConfig } from '../../../shared/ui/config.js';
import { useGenUiI18n, type GenUiTranslate } from '../../../shared/ui/i18n.js';
import type {
  RentalPlacesPort,
  RentalPlannerBlock,
  RentalPlannerSearch,
} from '@voll-intelligence/genui-contract';
import { RentalSearchOutcomeActions } from './rental_search_outcome.js';

type I18n = ReturnType<typeof useGenUiI18n>;

/** Digitação abaixo disto não consulta o host — o mínimo do `GET /rental/places`. */
const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;

/** "Seg, 21 set" — vazio quando o dia é ilegível. */
function weekdayDay(iso: string, locale: string): string {
  const day = parseDateOnly(iso);
  return day ? formatWeekdayDayMonth(day, locale, new Date()) : '';
}

/** "21–24 set" / "28 set – 2 out". */
function dayRange(from: string, to: string, locale: string): string {
  const start = parseDateOnly(from);
  const end = parseDateOnly(to);
  return start && end ? formatDateRange(start, end, locale, new Date()) : '';
}

/** "24 nov, 10:00" — uma ponta da janela, na régua da pílula. */
function pointLabel(value: string, locale: string): string {
  const at = parseLocalDateTime(value);
  return at ? `${formatDayMonth(at, locale)}, ${formatClockTime(at, locale)}` : value;
}

/** "24 nov, 10:00 – 26 nov, 10:00": a janela inteira, a régua da bancada. */
function rentalWindowLabel(pickupAt: string, dropoffAt: string, locale: string): string {
  const pickup = parseLocalDateTime(pickupAt);
  const dropoff = parseLocalDateTime(dropoffAt);
  return pickup && dropoff ? formatDateTimeWindow(pickup, dropoff, locale, new Date()) : '';
}

function daysLabel(t: GenUiTranslate, count: number): string {
  return t(count === 1 ? 'rentalSearch.days_one' : 'rentalSearch.days_other', { count });
}

/** A copy da barra da bancada, no catálogo da conversa (`rentalSearch.*`) — as
 *  mesmas frases da bancada do web-chat (`rentalBooking.search.*`). */
function plannerCopy({ t, locale }: I18n): RentalSearchCopy {
  return {
    driverLabel: t('rentalSearch.driver'),
    driverPicker: {
      searchPlaceholder: t('travelers.searchPlaceholder'),
      empty: t('travelers.empty'),
      error: t('travelers.searchError'),
      loadMore: t('travelers.loadMore'),
      you: t('travelers.you'),
      youRole: t('travelers.travelerRole'),
      selectedGroup: t('travelers.selectedGroup'),
      maxReached: t('travelers.maxReached', { max: 1 }),
      minReached: t('travelers.minReached'),
      backLabel: t('travelers.back'),
    },
    sameDropoffLabel: t('rentalSearch.sameDropoff'),
    swapLabel: t('rentalSearch.swap'),
    locationLabel: t('rentalSearch.location'),
    pickupLocationLabel: t('rentalSearch.pickupLocation'),
    dropoffLocationLabel: t('rentalSearch.dropoffLocation'),
    locationPlaceholder: t('rentalSearch.locationPlaceholder'),
    noPlacesLabel: t('rentalSearch.noPlaces'),
    placeErrorLabel: t('rentalSearch.placeError'),
    myLocationLabel: t('rentalSearch.myLocation'),
    myLocationErrorLabel: t('rentalSearch.myLocationError'),
    searchLabel: t('rentalSearch.submit'),
    backLabel: t('rentalSearch.back'),
    calendarLocale: locale,
    previousMonthLabel: t('rentalSearch.previousMonth'),
    nextMonthLabel: t('rentalSearch.nextMonth'),
    datesLabel: t('rentalSearch.when'),
    datesPlaceholder: t('rentalSearch.dates'),
    windowSummary: ({ pickupAt, dropoffAt }, days) =>
      `${rentalWindowLabel(pickupAt, dropoffAt, locale)} · ${daysLabel(t, days)}`,
    dateDraftSummary: ({ from, to }: DateRange, days) => {
      if (!from) return t('rentalSearch.chooseDates');
      if (!to || days === undefined) {
        return t('rentalSearch.chooseDropoff', { date: weekdayDay(from, locale) });
      }
      return `${weekdayDay(from, locale)} → ${weekdayDay(to, locale)} · ${daysLabel(t, days)}`;
    },
    confirmDatesLabel: ({ from, to }: DateRange) =>
      from && to
        ? t('rentalSearch.confirmDates', { range: dayRange(from, to, locale) })
        : t('rentalSearch.confirmDatesSheet'),
    confirmDatesSheetLabel: t('rentalSearch.confirmDatesSheet'),
    confirmAndSearchLabel: t('rentalSearch.confirmAndSearch'),
    pickupTimeLabel: t('rentalSearch.pickupTime'),
    dropoffTimeLabel: t('rentalSearch.dropoffTime'),
  };
}

/** O eco da busca — a fala que a pessoa aparenta ter dito: uma variante FECHADA
 *  por forma de devolução, nunca concatenação (a ordem muda por idioma). */
export function rentalSearchTranscript({ t, locale }: I18n, search: RentalPlannerSearch): string {
  if (!search.dropoff) {
    return t('rentalSearch.searchTextSame', {
      place: search.pickup.label,
      window: rentalWindowLabel(search.pickupAt, search.dropoffAt, locale),
    });
  }
  return t('rentalSearch.searchTextDifferent', {
    pickup: search.pickup.label,
    pickupAt: pointLabel(search.pickupAt, locale),
    dropoff: search.dropoff.label,
    dropoffAt: pointLabel(search.dropoffAt, locale),
  });
}

/** O que o agente já sabia, na forma da barra. Um lugar de devolução semeado (ou
 *  dito sem resolver) nasce com o switch desligado. */
function initialValue(ui: RentalPlannerBlock): RentalSearchValue {
  const prefill = ui.prefill ?? {};
  const placeOf = (place: { id: string; label: string } | undefined) =>
    place ? { id: place.id, label: place.label } : undefined;
  const pickupPlace = placeOf(prefill.pickup);
  const dropoffPlace = placeOf(prefill.dropoff);
  return {
    dropoffMode: prefill.dropoff || prefill.dropoffTerm ? 'different' : 'same',
    ...(pickupPlace ? { pickupPlace } : {}),
    ...(dropoffPlace ? { dropoffPlace } : {}),
    ...(prefill.pickupAt ? { pickupAt: prefill.pickupAt } : {}),
    ...(prefill.dropoffAt ? { dropoffAt: prefill.dropoffAt } : {}),
  };
}

/** Quem conversa, a linha "Você" do QUEM. Sem `requesterId` (blocos antigos)
 *  o id é o próprio nome: o QUEM só lê, e dirige quem conversa. */
function requesterOf(ui: RentalPlannerBlock): PersonItem | undefined {
  if (!ui.driverName) return undefined;
  return { id: ui.requesterId ?? ui.driverName, name: ui.driverName };
}

/**
 * O QUEM do card: o condutor (o que a busca anterior levou, ou quem conversa) e
 * a busca do roster pelo port `travelers` do host, a mesma do voo e do ônibus.
 * Sem `requesterId` ou com o card travado não há escolha: o condutor só lê.
 */
function useCardDriver(ui: RentalPlannerBlock, live: boolean) {
  const requester = requesterOf(ui);
  const [chosen, setChosen] = useState<PersonItem | undefined>(ui.prefill?.driver);
  const driver = chosen ?? requester;
  const editable = live && ui.requesterId !== undefined;
  const { search, photos } = useTravelerFieldSearch({
    live: editable,
    directory: useGenUiConfig().ports?.travelers,
    requester,
    travelers: driver ? [driver] : [],
  });
  const photoUrl = driver ? photos.get(driver.id) : undefined;
  return {
    driver: driver ? { ...driver, ...(photoUrl ? { photoUrl } : {}) } : undefined,
    /** O condutor da busca, só quando não é quem conversa. */
    colleague: driver && driver.id !== requester?.id ? driver : undefined,
    ...(editable
      ? {
          search,
          onChange: (next: PersonItem) => {
            setChosen(next.id === requester?.id ? undefined : next);
          },
        }
      : {}),
  };
}

/** Os termos ditos e não resolvidos, para os campos vazios da barra. */
function placeDraftsOf(ui: RentalPlannerBlock): {
  placeDrafts?: { pickup?: string; dropoff?: string };
} {
  const { pickupTerm, dropoffTerm } = ui.prefill ?? {};
  if (!pickupTerm && !dropoffTerm) return {};
  return {
    placeDrafts: {
      ...(pickupTerm ? { pickup: pickupTerm } : {}),
      ...(dropoffTerm ? { dropoff: dropoffTerm } : {}),
    },
  };
}

/** A busca que o Buscar leva; `null` com algo faltando (a barra já não deixa).
 *  `driver` só quando dirige um colega. */
function searchOf(
  value: RentalSearchValue,
  colleague: PersonItem | undefined,
): RentalPlannerSearch | null {
  const { pickupPlace, dropoffPlace, pickupAt, dropoffAt } = value;
  if (!pickupPlace || !pickupAt || !dropoffAt) return null;
  const pickup = { id: pickupPlace.id, label: pickupPlace.label };
  const driver = colleague ? { driver: { id: colleague.id, name: colleague.name } } : {};
  if (value.dropoffMode === 'same') return { pickup, pickupAt, dropoffAt, ...driver };
  if (!dropoffPlace) return null;
  return {
    pickup,
    dropoff: { id: dropoffPlace.id, label: dropoffPlace.label },
    pickupAt,
    dropoffAt,
    ...driver,
  };
}

/**
 * Um ciclo de busca de lugar CONTROLADO pelo card (a barra pede um por campo):
 * o `useRemoteSearch` do kit sobre o port do host. Abaixo de `MIN_CHARS` o campo
 * fica parado, sem consultar. Sem port, a lista vem vazia.
 */
function useRentalPlaceSearch(
  port: RentalPlacesPort | null,
): PlaceFieldControlledSearch<PlaceSuggestion> {
  const [term, setTerm] = useState('');
  const live = term.trim();
  const { state, results } = useRemoteSearch<PlaceSuggestion>({
    enabled: live.length >= MIN_CHARS,
    term,
    debounceMs: DEBOUNCE_MS,
    search: async (query) => {
      const places = port ? await port.searchPlaces(query) : [];
      return places.map((place) => ({
        id: place.ref,
        label: place.title,
        ...(place.subtitle ? { description: place.subtitle } : {}),
        icon: place.airport ? ('airport' as const) : ('place' as const),
      }));
    },
  });
  return { state, suggestions: results, onQueryChange: setTerm };
}

/** O "Minha localização" do port, no formato do kit; sem `placeAt`, nada — e a
 *  linha não aparece no painel. */
function usePlaceAt(port: RentalPlacesPort | null): RentalPlaceAt | undefined {
  const placeAt = port?.placeAt;
  return useMemo(
    () =>
      placeAt
        ? async (position) => {
            const place = await placeAt(position);
            return place ? { id: place.ref, label: place.title } : null;
          }
        : undefined,
    [placeAt],
  );
}

type PlannerProps = Readonly<{
  ui: RentalPlannerBlock;
  readOnly?: boolean;
  superseded?: boolean;
  replay?: boolean;
  onSearch: (search: RentalPlannerSearch) => void;
}>;

/** A barra da bancada no card, semeada com o que o agente já sabia. */
function RentalPlannerBar({
  ui,
  readOnly = false,
  superseded = false,
  replay = false,
  onSearch,
}: PlannerProps): ReactNode {
  const i18n = useGenUiI18n();
  const config = useGenUiConfig();
  const port = config.ports?.rentalPlaces ?? null;
  const [value, setValue] = useState(() => initialValue(ui));
  const pickupSearch = useRentalPlaceSearch(port);
  const dropoffSearch = useRentalPlaceSearch(port);
  const location = useRentalMyLocation(value, setValue, usePlaceAt(port));
  const who = useCardDriver(ui, !readOnly && !superseded);
  return (
    <div className="rounded-xl border border-border bg-card p-3 shadow-(--shadow-elevation)">
      <RentalSearchBar
        value={value}
        copy={plannerCopy(i18n)}
        {...(who.driver ? { driver: who.driver } : {})}
        {...(who.search ? { driverSearch: who.search } : {})}
        {...(who.onChange ? { onDriverChange: who.onChange } : {})}
        pickupSearch={pickupSearch}
        dropoffSearch={dropoffSearch}
        {...placeDraftsOf(ui)}
        busy={readOnly}
        guided={!superseded}
        guidedOpen={replay ? 'never' : 'always'}
        {...(location.myLocation ? { myLocation: location.myLocation } : {})}
        {...(location.onUseMyLocation ? { onUseMyLocation: location.onUseMyLocation } : {})}
        onChange={location.onValueChange}
        onSubmit={(next) => {
          const search = searchOf(next, who.colleague);
          if (search) onSearch(search);
        }}
      />
    </div>
  );
}

/**
 * O card de busca de carro da conversa (`rental_planner`, #5641): a
 * `RentalSearchBar` da bancada num card — o QUEM (o condutor) e o switch
 * "Devolução no mesmo local" na cabeça, o(s) lugar(es) e o Quando na fileira. Emitido SEMPRE
 * antes da primeira busca; a busca sai só do Buscar (`onSearch`), com os lugares
 * como ref. Card congelado: a barra fica travada.
 *
 * O preenchimento guiado é o dos outros cards de busca: só no card que ainda é o
 * último da conversa, e o remontado do histórico não abre nada ao nascer.
 *
 * O cartão de uma busca sem carros (`outcome`, #5642) nasce recolhido nas saídas
 * (`RentalSearchOutcomeActions`): "Tentar novamente" manda a busca semeada pelo
 * mesmo `onSearch` do Buscar, e "Mudar datas ou horários" abre a barra ali mesmo,
 * semeada — sem turno; a busca nova sai do Buscar dela.
 */
export function RentalPlannerCard({
  note,
  ...props
}: PlannerProps & Readonly<{ note?: string }>): ReactNode {
  const config = useGenUiConfig();
  const [opened, setOpened] = useState(false);
  const { ui, readOnly = false, superseded = false, onSearch } = props;
  const seeded = searchOf(initialValue(ui), ui.prefill?.driver);
  return (
    <div className="genui2 grid gap-3">
      {note && (
        <div className="itin-intro">
          {config.renderMarkdown ? config.renderMarkdown(note) : note}
        </div>
      )}
      {ui.outcome && !opened ? (
        <RentalSearchOutcomeActions
          outcome={ui.outcome}
          disabled={readOnly || superseded}
          onRetry={
            seeded
              ? () => {
                  onSearch(seeded);
                }
              : undefined
          }
          onChange={() => {
            setOpened(true);
          }}
        />
      ) : (
        <RentalPlannerBar {...props} />
      )}
    </div>
  );
}
